import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

import produce from 'immer';
import { orderBy } from 'lodash';
import qs from 'qs';
import { FormProvider, useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { useHistory, useLocation } from 'react-router-dom';
import { sortAlphabeticalArray } from 'utilitys/helpers/general';

import { useQuerystring } from 'hooks/useQuerystring';

import { useBattleCardsStore } from 'store/battlecards.store';
import { useProjectTreeStore } from 'store/projectContract.store';
import { useVersionStore } from 'store/version.store';

import { useQueryGetSingleMilestoneFragnet } from 'api/queries/getMilestoneFragnet.query';
import { QueryResponse, queryFn as getSingleWbs } from 'api/queries/getWbs.query';
import { QueryKeys } from 'api/queryKeys';

import Icon from 'components/common/Icons/icon';
import { ToggleView } from 'components/common/ToggleView/ToggleView';
import { Separator } from 'components/common/WidgetWithTitle/Separator/separator';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';

import { InitialFilters } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainFilters/InitialFilters/InitialFilters';
import {
    FinalData,
    IFields,
    IFieldsOptions,
} from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainFilters/MainFilters.types';
import {
    CustomizeFilterOptions,
    activityCodeMapperUID,
    buildDataForQueryString,
    buildFilterPayload,
    defaultLagLeadValues,
    defaultListOptionsForTrueFalse,
    getFlags,
    mapper,
    milestoneFragnetMapper,
    riskIndicatorsList,
    statusListOptions,
} from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainFilters/MainFilters.utils';
import { buildPayloadByKey } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainFilters/MainFiltersPayloadConfig';
import { OptionalFilters } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainFilters/OptionalFilters/OptionalFilters';
import { buildRowsBoardFilter } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/RowsBoard/RowsBoardColumns.utils';
import { BulkAssignWrapper } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/bulkAssign/bulkAssignWrapper';
import { ActivitiesSegments } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/components/ActivitiesSegments/ActivitiesSegments';
import CustomizeFilter from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/customizeFilter/customizeFilter';
import { assigneeMapper } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/helper';
import { CustomizedButton } from 'components/common';

import classes from './MainFilters.module.scss';

export const MainFilters = memo(({ boardInit, assigneeOptions }: any) => {
    const queryClient = useQueryClient();
    const [customizeFilterOptions, setCustomizeFilterOptions] = useState(CustomizeFilterOptions);
    const { setFilterData, setWidgetFiltersData } = useBattleCardsStore();
    const displayStyle = useBattleCardsStore((state) => state.displayStyle);
    const { setDisplayStyle } = useBattleCardsStore();
    const { project, contract } = useProjectTreeStore();
    const currentVersion = useVersionStore((state) => state.version);

    const history = useHistory();
    const { search } = useLocation();

    const latestVersionId = currentVersion?.id;

    const { getQueryKey, removeAllQueryKey } = useQuerystring();

    const qActivityFragnet = useMemo(() => {
        return getQueryKey({ key: 'activityFragnet' });
    }, []);

    const qActivityForwardFragnet = useMemo(() => {
        return getQueryKey({ key: 'activityForwardFragnet' });
    }, []);

    const { data: activityFragnettSingleData } = useQueryGetSingleMilestoneFragnet({
        latestVersion: latestVersionId,
        taskVersionHashCode: qActivityFragnet,
    });

    const { data: activityForwardFragnettSingleData } = useQueryGetSingleMilestoneFragnet({
        latestVersion: latestVersionId,
        taskVersionHashCode: qActivityForwardFragnet,
    });

    const fieldsOptions: IFieldsOptions = {
        typeCardOptions: boardInit.cardType.map(mapper),
        activityFragnetOptions: activityFragnettSingleData ?? boardInit.fragnetMilestone.map(milestoneFragnetMapper),
        completionPlanSegmentOptions: boardInit.fragnetCompletion.map(mapper),
        cardActivityTypeOptions: boardInit.cardActivityType.map(mapper),
        assigneeOptions: assigneeOptions,
        updatedByOptions: sortAlphabeticalArray(boardInit.assignee.map(assigneeMapper), 'label', 'asc'),
        tagsOptions: boardInit.tags.map((item) => ({ label: item.name, value: { name: item.name, isExclude: false } })),
        activityCodesOptions: orderBy(
            boardInit.activityCode.map(activityCodeMapperUID),
            [(item: any) => item['label'].toLowerCase()],
            ['asc'],
        ),
        trackedActivityOptions: defaultListOptionsForTrueFalse,
        hasAttachmentsOptions: defaultListOptionsForTrueFalse,
        lagLeadOptions: defaultLagLeadValues,
        importantOptions: boardInit.cardImportant.map(mapper),
        cardUrgentOptions: boardInit.cardUrgent.map(mapper),
        statusOptions: statusListOptions,
        resourceTypeOptions: boardInit.resourceType.map(mapper),
        riskIndicatorsOptions: riskIndicatorsList,
        resourceNameOptions: boardInit.cardResource.map((i) => ({
            ...i,
            label: `${i.label} (~${i.count} related activities)`,
        })),
        clusters: boardInit.clusters,
    };

    const form = useForm<IFields>({
        defaultValues: {
            project: project?.id,
            contract: contract?.id,
            version: latestVersionId,
            cardActivity: null,
            assignee: null,
            status: null,
            riskIndicators: null,
            important: null,
            urgent: null,
            tags: null,
            cardSearch: '',
            flags: { isAdvancedTextSearch: false },
            finishDate: { value: [], type: 'TODAY_REF' },
            activityCodes: null,
            wbs: '',
            // Optional
            activityFragnet: null,
            activityForwardFragnet: null,
            cardType: null,
            completionPlanSegment: null,
            duration: null,
            float: null,
            hasAttachments: null,
            hasComments: { value: [], type: 'TODAY_REF' },
            lagLead: null,
            relatedActivity: '',
            resourceType: null,
            startDate: { value: [], type: 'TODAY_REF' },
            submittedCompletion: [0, 100],
            trackedActivity: null,
            updatedBy: null,
            commenter: null,
            mentioned: null,
            wbsLevel: [1, 10],
            resourceName: null,
            baselineStartDate: { value: [], type: 'TODAY_REF' },
            baselineEndDate: { value: [], type: 'TODAY_REF' },
            cluster: null,
        },
    });

    useEffect(() => {
        search.length === 0 &&
            history.replace({
                search: '?cardActivity=TASK_DEPENDENT,START_MILESTONE',
            });
    }, []);

    // First Load - select customizeFilterOptions by query string
    useEffect(() => {
        setCustomizeFilterOptions(
            produce(CustomizeFilterOptions, (draft) => {
                Object.keys(draft).forEach((item) => {
                    if (getQueryKey({ key: item })) {
                        draft[item].isSelected = true;
                    }
                });
                if (getQueryKey({ key: 'startDatesRange' })) {
                    draft.startDate.isSelected = true;
                }

                if (getQueryKey({ key: 'baselineStartDatesRange' })) {
                    draft.baselineStartDate.isSelected = true;
                }
                if (getQueryKey({ key: 'baselineEndDatesRange' })) {
                    draft.baselineEndDate.isSelected = true;
                }

                if (getQueryKey({ key: 'commentRange' })) {
                    draft.hasComments.isSelected = true;
                }
            }),
        );
    }, []);

    const isActivityFragnettSingleDataReady =
        (!qActivityFragnet && latestVersionId) || (latestVersionId && activityFragnettSingleData);

    const isActivityForwardFragnettSingleDataReady =
        (!qActivityForwardFragnet && latestVersionId) || (latestVersionId && activityForwardFragnettSingleData);

    useEffect(() => {
        if (isActivityFragnettSingleDataReady || isActivityForwardFragnettSingleDataReady) {
            form.reset({
                project: project?.id,
                contract: contract?.id,
                version: latestVersionId,
                cardActivity: buildPayloadByKey.cardActivity.getValueByQuery({
                    value: getQueryKey({ key: 'cardActivity' })?.split(','),
                    options: fieldsOptions.cardActivityTypeOptions,
                }),
                assignee: buildPayloadByKey.assignee.getValueByQuery({
                    value: getQueryKey({ key: 'assignee' }),
                    options: fieldsOptions.assigneeOptions,
                }),
                status: buildPayloadByKey.status.getValueByQuery({
                    value: getQueryKey({ key: 'status' })?.split(','),
                    options: fieldsOptions.statusOptions,
                }),
                riskIndicators: buildPayloadByKey.riskIndicators.getValueByQuery({
                    value: getQueryKey({ key: 'riskIndicators' })?.split(','),
                    options: fieldsOptions.riskIndicatorsOptions,
                }),
                important: buildPayloadByKey.important.getValueByQuery({
                    value: getQueryKey({ key: 'important' }),
                    options: fieldsOptions.importantOptions,
                }),
                urgent: buildPayloadByKey.urgent.getValueByQuery({
                    value: getQueryKey({ key: 'urgent' }),
                    options: fieldsOptions.cardUrgentOptions,
                }),
                tags: buildPayloadByKey.tags.getValueByQuery({
                    tagsValue: qs.parse(window.location.search, { ignoreQueryPrefix: true })['tags'],
                    excludeTagsValue: qs.parse(window.location.search, { ignoreQueryPrefix: true })['excludeTags'],
                    options: fieldsOptions.tagsOptions,
                }),
                cardSearch:
                    (qs.parse(window.location.search, {
                        ignoreQueryPrefix: true,
                    }).cardSearch as string) || '',

                flags: getFlags({
                    isAdvancedTextSearch: getQueryKey({ key: 'isAdvancedTextSearch' }) === 'true',
                    endDatesRange: getQueryKey({ key: 'endDatesRange' }),
                    startDatesRange: getQueryKey({ key: 'startDatesRange' }),
                    commentRange: getQueryKey({ key: 'commentRange' }),
                    baselineStartDatesRange: getQueryKey({ key: 'baselineStartDatesRange' }),
                    baselineEndDatesRange: getQueryKey({ key: 'baselineEndDatesRange' }),
                }),

                finishDate: buildPayloadByKey.finishDate.getValueByQuery({
                    value: getQueryKey({ key: 'finishDate' }),
                    flagValue: getQueryKey({ key: 'endDatesRange' }),
                }),

                activityCodes: buildPayloadByKey.activityCodes.getValueByQuery({
                    activityCodesValue: getQueryKey({ key: 'activityCodes' })?.split(','),
                    excludeActivityCodesValue: getQueryKey({ key: 'excludeActivityCodes' })?.split(','),
                    options: fieldsOptions.activityCodesOptions,
                }),

                wbs: getQueryKey({ key: 'wbs' }) || '',

                // Optional
                activityFragnet:
                    activityFragnettSingleData &&
                    activityFragnettSingleData
                        .map(milestoneFragnetMapper)
                        .find((item) => item.value === qActivityFragnet),

                activityForwardFragnet:
                    activityForwardFragnettSingleData &&
                    activityForwardFragnettSingleData
                        .map(milestoneFragnetMapper)
                        .find((item) => item.value === qActivityForwardFragnet),

                cardType: buildPayloadByKey.cardType.getValueByQuery({
                    value: getQueryKey({ key: 'cardType' })?.split(','),
                    options: fieldsOptions.typeCardOptions,
                }),

                completionPlanSegment: buildPayloadByKey.completionPlanSegment.getValueByQuery({
                    value: getQueryKey({ key: 'completionPlanSegment' }),
                    options: fieldsOptions.completionPlanSegmentOptions,
                }),

                duration: getQueryKey({ key: 'duration' })?.split(',').map(Number) || null,

                float: getQueryKey({ key: 'float' })?.split(',').map(Number) || null,

                hasAttachments: buildPayloadByKey.hasAttachments.getValueByQuery({
                    value: getQueryKey({ key: 'hasAttachments' }),
                    options: fieldsOptions.hasAttachmentsOptions,
                }),

                hasComments: buildPayloadByKey.hasComments.getValueByQuery({
                    value: getQueryKey({ key: 'hasComments' }),
                    flagValue: getQueryKey({ key: 'commentRange' }),
                }),

                lagLead: buildPayloadByKey.lagLead.getValueByQuery({
                    value: getQueryKey({ key: 'lagLead' }),
                    options: fieldsOptions.lagLeadOptions,
                }),

                relatedActivity: getQueryKey({ key: 'relatedActivity' }) || '',

                resourceType: buildPayloadByKey.resourceType.getValueByQuery({
                    value: getQueryKey({ key: 'resourceType' }),
                    options: fieldsOptions.resourceTypeOptions,
                }),

                startDate: buildPayloadByKey.startDate.getValueByQuery({
                    value: getQueryKey({ key: 'startDate' }),
                    flagValue: getQueryKey({ key: 'startDatesRange' }),
                }),

                submittedCompletion: getQueryKey({ key: 'submittedCompletion' })?.split(',').map(Number) || [0, 100],

                trackedActivity: buildPayloadByKey.trackedActivity.getValueByQuery({
                    value: getQueryKey({ key: 'trackedActivity' }),
                    options: fieldsOptions.trackedActivityOptions,
                }),

                updatedBy: buildPayloadByKey.updatedBy.getValueByQuery({
                    value: getQueryKey({ key: 'updatedBy' }),
                    options: fieldsOptions.updatedByOptions,
                }),

                commenter: buildPayloadByKey.commenter.getValueByQuery({
                    value: getQueryKey({ key: 'commenter' }),
                    options: fieldsOptions.updatedByOptions,
                }),

                mentioned: buildPayloadByKey.mentioned.getValueByQuery({
                    value: getQueryKey({ key: 'mentioned' }),
                    options: fieldsOptions.updatedByOptions,
                }),

                wbsLevel: getQueryKey({ key: 'wbsLevel' })?.split(',').map(Number) || [0, 10],

                resourceName: buildPayloadByKey.resourceName.getValueByQuery({
                    value: getQueryKey({ key: 'resourceName' })?.split(','),
                    options: fieldsOptions.resourceNameOptions,
                }),

                baselineStartDate: buildPayloadByKey.baselineStartDate.getValueByQuery({
                    value: getQueryKey({ key: 'baselineStartDate' }),
                    flagValue: getQueryKey({ key: 'baselineStartDatesRange' }),
                }),

                baselineEndDate: buildPayloadByKey.baselineEndDate.getValueByQuery({
                    value: getQueryKey({ key: 'baselineEndDate' }),
                    flagValue: getQueryKey({ key: 'baselineEndDatesRange' }),
                }),

                cluster: buildPayloadByKey.cluster.getValueByQuery({
                    value: getQueryKey({ key: 'cluster' }),
                    options: fieldsOptions.clusters,
                }),
            });

            form.handleSubmit(handleApplyFilters)();
        }
    }, [latestVersionId, activityFragnettSingleData, qActivityFragnet, activityForwardFragnettSingleData]);

    const handleCustomizeFilterOptions = (value) => {
        if (customizeFilterOptions[value].isSelected) {
            if (
                value === 'hasComments' ||
                value === 'startDate' ||
                value === 'baselineStartDate' ||
                value === 'baselineEndDate'
            ) {
                const flags = { ...form.watch('flags') };
                value === 'hasComments' && delete flags.commentRange;
                value === 'startDate' && delete flags.startDatesRange;
                value === 'baselineStartDate' && delete flags.baselineStartDatesRange;
                value === 'baselineEndDate' && delete flags.baselineEndDatesRange;
                form.setValue('flags', flags);
                form.setValue(value, { value: [], type: 'TODAY_REF' });
            } else {
                form.setValue(value, null);
            }

            form.handleSubmit(handleApplyFilters)();
        }

        setCustomizeFilterOptions(
            produce(customizeFilterOptions, (draft) => {
                draft[value].isSelected = !draft[value].isSelected;
                return draft;
            }),
        );
    };

    const handleClear = () => {
        form.reset({
            project: project?.id,
            contract: contract?.id,
            version: latestVersionId,
            cardActivity: null,
            assignee: null,
            status: null,
            riskIndicators: null,
            important: null,
            urgent: null,
            tags: null,
            cardSearch: '',
            flags: { isAdvancedTextSearch: false },
            finishDate: { value: [], type: 'TODAY_REF' },
            activityCodes: null,
            wbs: '',
            // Optional
            activityFragnet: null,
            activityForwardFragnet: null,
            cardType: null,
            completionPlanSegment: null,
            duration: null,
            float: null,
            hasAttachments: null,
            hasComments: { value: [], type: 'TODAY_REF' },
            lagLead: null,
            relatedActivity: '',
            resourceType: null,
            startDate: { value: [], type: 'TODAY_REF' },
            submittedCompletion: [0, 100],
            trackedActivity: null,
            updatedBy: null,
            commenter: null,
            mentioned: null,
            wbsLevel: [1, 10],
            resourceName: null,
            baselineStartDate: { value: [], type: 'TODAY_REF' },
            baselineEndDate: { value: [], type: 'TODAY_REF' },
            cluster: null,
        });

        form.handleSubmit(handleApplyFilters)();
    };

    const removeUnSelectedFiltersFromData = (data: IFields): FinalData => {
        return produce(data, (draft) => {
            for (const property in customizeFilterOptions) {
                if (customizeFilterOptions.hasOwnProperty(property) && !customizeFilterOptions[property].isSelected) {
                    delete draft[property];
                }
            }
            return draft;
        });
    };

    const setQuery = useCallback(
        (query, value) => {
            const existingQueries = qs.parse(window.location.search, {
                ignoreQueryPrefix: true,
            });

            const queryString = qs.stringify(
                { ...existingQueries, [query]: value },
                { arrayFormat: 'comma', skipNulls: true, encode: true },
            );
            history.replace({
                search: queryString,
            });
        },
        [history],
    );

    const handleApplyFilters = async (data: IFields) => {
        removeAllQueryKey();
        const finalData = removeUnSelectedFiltersFromData(data);

        const payload = buildFilterPayload(finalData);
        const queryStringData = buildDataForQueryString(finalData);

        for (const key in queryStringData) {
            queryStringData[key] && setQuery(key, queryStringData[key]);
        }
        setFilterData(payload);
        const params = { latestVersion: latestVersionId, taskId: data.wbs };

        const singleWbs = await queryClient.fetchQuery<
            QueryResponse[],
            QueryError,
            QueryResponse[],
            [QueryKeys, unknown]
        >(['getSingleWbs', params], () => getSingleWbs(params));

        // Set Widget Filters Data to store
        const widgetFiltersData = buildRowsBoardFilter({
            filter: { filters: payload },
            activityCodes: boardInit.activityCode,
            activityFragnet: boardInit.fragnetMilestone,
            assigneeOptionsList: boardInit.assignee,
            singleWbs: singleWbs,
        });

        setWidgetFiltersData(widgetFiltersData);
    };

    return (
        <FormProvider {...form}>
            <div data-testid={'main-filters'} className={classes.mainFiltersContainer}>
                <form onSubmit={form.handleSubmit(handleApplyFilters)}>
                    <WidgetWithTitle
                        className={classes.filtersWidgetWithTitleContainer}
                        title={'Filters'}
                        titleComponents={[
                            <CustomizeFilter
                                listOptions={customizeFilterOptions}
                                onSelection={handleCustomizeFilterOptions}
                            />,
                            <CustomizedButton
                                size={'medium'}
                                color={'primary'}
                                type={'submit'}
                                className={classes.hideButton}
                            >
                                Apply Filters
                            </CustomizedButton>,
                            <CustomizedButton
                                startIcon={<Icon name={'filter-clear'} size="1.5rem" color={'#2C8FA5'} />}
                                color={'secondary'}
                                onClick={handleClear}
                                data-testid={'clear-filter'}
                            >
                                Clear Filter
                            </CustomizedButton>,
                            <ActivitiesSegments />,
                            <Separator className={classes.separator} />,
                            <BulkAssignWrapper />,
                            <ToggleView displayStyle={displayStyle} setDisplayStyle={setDisplayStyle} />,
                        ]}
                    >
                        <div className={classes.filtersContainer}>
                            <InitialFilters fieldsOptions={fieldsOptions} handleApplyFilters={handleApplyFilters} />
                            <OptionalFilters
                                customizeFilterOptions={customizeFilterOptions}
                                fieldsOptions={fieldsOptions}
                                handleApplyFilters={handleApplyFilters}
                            />
                        </div>
                    </WidgetWithTitle>
                </form>
            </div>
        </FormProvider>
    );
});
